import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell,
  Plus,
  Users,
  Calendar,
  MapPin,
  Clock,
  MoreVertical,
  Settings,
  UserPlus,
  Trash2,
  AlertTriangle,
  UserMinus
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Home = () => {
  const { user, userProfile } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGroupMenu, setShowGroupMenu] = useState(null);

  // Confirmation modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmData, setDeleteConfirmData] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaveConfirmData, setLeaveConfirmData] = useState(null);

  // Fetch user's groups
  const fetchGroups = useCallback(async () => {
    if (!user || !userProfile) return;
    
    try {
      setLoading(true);
      const token = await user.getIdToken();
      // Add cache-busting parameter to prevent browser caching
      const cacheBuster = new Date().getTime();
      const response = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/groups/user/${userProfile.user_id}?_t=${cacheBuster}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch groups');
      }

      const data = await response.json();
      setGroups(data.data || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, [user, userProfile]);

  // Delete group
  const deleteGroup = async (groupId, groupName) => {
    setDeleteConfirmData({ groupId, groupName });
    setDeleteConfirmText('');
    setShowDeleteConfirm(true);
    setShowGroupMenu(null); // Close the group menu
  };

  // Execute delete after confirmation
  const executeDelete = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type "DELETE" exactly to confirm');
      return;
    }

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/groups/${deleteConfirmData.groupId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete group');
      }

      toast.success('Group deleted successfully!');
      setShowDeleteConfirm(false);
      setDeleteConfirmData(null);
      setDeleteConfirmText('');
      fetchGroups(); // Refresh groups list
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Failed to delete group');
    }
  };

  // Leave group
  const leaveGroup = async (groupId, groupName) => {
    setLeaveConfirmData({ groupId, groupName });
    setShowLeaveConfirm(true);
    setShowGroupMenu(null); // Close the group menu
  };

  // Execute leave after confirmation
  const executeLeave = async () => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/groups/${leaveConfirmData.groupId}/leave`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to leave group');
      }

      toast.success('Left group successfully!');
      setShowLeaveConfirm(false);
      setLeaveConfirmData(null);
      fetchGroups(); // Refresh groups list
    } catch (error) {
      console.error('Error leaving group:', error);
      toast.error('Failed to leave group');
    }
  };

  useEffect(() => {
    if (user && userProfile) {
      fetchGroups();
    }
  }, [user, userProfile, fetchGroups]);

  // Mock data for upcoming plans
  const mockPlans = [
    {
      id: 1,
      title: "Weekend Movie Night",
      group: "College Friends",
      date: "Dec 16",
      time: "7:00 PM",
      status: "voting open",
      statusColor: "bg-orange-100 text-orange-800",
      votesNeeded: 2,
      participants: 6,
      icon: "🎬"
    },
    {
      id: 2,
      title: "Brunch & Shopping",
      group: "Work Squad",
      date: "Dec 18",
      time: "11:00 AM",
      status: "finalized",
      statusColor: "bg-green-100 text-green-800",
      location: "Downtown Mall",
      participants: 4,
      icon: "🛍️"
    },
    {
      id: 3,
      title: "Hiking Adventure",
      group: "Gym Buddies",
      date: "Dec 22",
      time: "8:00 AM",
      status: "planning",
      statusColor: "bg-blue-100 text-blue-800",
      participants: 3,
      icon: "🎒"
    }
  ];


  return (
    <div className="space-y-6">
        {/* Greeting Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Hi {userProfile?.display_name || userProfile?.name || 'User'}! 👋
          </h1>
          <p className="text-gray-600 text-lg mb-6">
            Ready to plan your next adventure?
          </p>
          
          {/* Create New Plan Button */}
          <Link
            to="/generate"
            className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white py-4 px-6 rounded-xl font-semibold text-lg flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <Plus className="w-6 h-6" />
            <span>Create New Plan</span>
          </Link>
        </motion.div>

        {/* Pending Votes Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-blue-50 border border-blue-200 rounded-xl p-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Bell className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-semibold text-gray-900">3 pending votes</p>
                <p className="text-sm text-gray-600">Your friends are waiting for your decision</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">3</span>
              </div>
              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
            </div>
          </div>
        </motion.div>

        {/* Your Groups Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Your Groups</h2>
            <Link to="/groups" className="text-blue-600 font-medium text-sm">
              View All
            </Link>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-2 gap-4">
              {[1, 2].map((i) => (
                <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 animate-pulse">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex -space-x-2">
                      <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                      <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                      <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                    </div>
                    <div className="w-5 h-5 bg-gray-200 rounded"></div>
                  </div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded mb-1 w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 mb-4">No groups yet</p>
              <Link
                to="/groups"
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Create Your First Group
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {groups.slice(0, 2).map((group) => (
                <motion.div
                  key={group.group_id}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 relative"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex -space-x-2">
                      {[...Array(Math.min(3, group.member_count))].map((_, index) => (
                        <div
                          key={index}
                          className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center border-2 border-white"
                        >
                          <span className="text-blue-600 font-semibold text-xs">
                            {String.fromCharCode(65 + index)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="relative">
                      <button
                        onClick={() => setShowGroupMenu(showGroupMenu === group.group_id ? null : group.group_id)}
                        className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <MoreVertical className="w-4 h-4 text-gray-400" />
                      </button>
                      
                      {showGroupMenu === group.group_id && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10"
                        >
                          {/* Everyone can create plans */}
                          <Link
                            to={`/generate?groupId=${group.group_id}`}
                            className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <Calendar className="w-4 h-4" />
                            <span>Create Plan</span>
                          </Link>
                          
                          {/* Admin-only options */}
                          {group.role === 'admin' && (
                            <>
                              <Link
                                to="/groups"
                                className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <Settings className="w-4 h-4" />
                                <span>Manage</span>
                              </Link>
                              <hr className="my-1" />
                              <button
                                onClick={() => deleteGroup(group.group_id, group.name)}
                                className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                                <span>Delete</span>
                              </button>
                            </>
                          )}
                          
                          {/* Everyone can leave (but admins see it at bottom) */}
                          {group.role !== 'admin' && (
                            <>
                              <hr className="my-1" />
                              <button
                                onClick={() => leaveGroup(group.group_id, group.name)}
                                className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <UserPlus className="w-4 h-4" />
                                <span>Leave</span>
                              </button>
                            </>
                          )}
                          
                          {/* Admin leave option (at bottom after delete) */}
                          {group.role === 'admin' && (
                            <>
                              <hr className="my-1" />
                              <button
                                onClick={() => leaveGroup(group.group_id, group.name)}
                                className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <UserPlus className="w-4 h-4" />
                                <span>Leave</span>
                              </button>
                            </>
                          )}
                        </motion.div>
                      )}
                    </div>
                  </div>
                  <Link to={`/generate?groupId=${group.group_id}`}>
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                        {group.name}
                      </h3>
                      {group.role === 'admin' && (
                        <span className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full font-medium">
                          Admin
                        </span>
                      )}
                    </div>
                  </Link>
                  <p className="text-sm text-gray-600 mb-2">{group.member_count} members</p>
                  <p className="text-xs text-gray-500">
                    Created {new Date(group.created_at).toLocaleDateString()}
                  </p>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Upcoming Plans Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Upcoming Plans</h2>
            <Link to="/plans" className="text-blue-600 font-medium text-sm">
              View All
            </Link>
          </div>
          
          <div className="space-y-4">
            {mockPlans.map((plan) => (
              <motion.div
                key={plan.id}
                whileHover={{ scale: 1.01 }}
                className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="text-2xl">{plan.icon}</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{plan.title}</h3>
                      <p className="text-sm text-gray-600">{plan.group}</p>
                    </div>
                  </div>
                  <MoreVertical className="w-5 h-5 text-gray-400" />
                </div>
                
                <div className="flex items-center space-x-4 mb-3">
                  <div className="flex items-center space-x-1 text-sm text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>{plan.date} • {plan.time}</span>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${plan.statusColor}`}>
                    {plan.status}
                  </span>
                </div>
                
                {plan.votesNeeded && (
                  <div className="flex items-center space-x-1 text-sm text-red-600 mb-3">
                    <Clock className="w-4 h-4" />
                    <span>{plan.votesNeeded} more votes needed</span>
                  </div>
                )}
                
                {plan.location && (
                  <div className="flex items-center space-x-1 text-sm text-gray-600 mb-3">
                    <MapPin className="w-4 h-4" />
                    <span>{plan.location}</span>
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{plan.participants}</span>
                  </div>
                  {plan.status === 'voting open' && (
                    <button className="bg-yellow-500 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-yellow-600 transition-colors">
                      Vote Now
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl"
              >
                <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-6">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
                  Delete Group Permanently
                </h2>
                
                <div className="text-center mb-6">
                  <p className="text-gray-600 mb-4">
                    Are you sure you want to delete <strong>"{deleteConfirmData?.groupName}"</strong>?
                  </p>
                  
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left">
                    <p className="text-sm text-red-800 font-medium mb-2">This action will:</p>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>• Remove the group permanently</li>
                      <li>• Remove all group members</li>
                      <li>• Delete all group data</li>
                      <li>• <strong>This action CANNOT be undone</strong></li>
                    </ul>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type <span className="font-mono bg-gray-100 px-2 py-1 rounded">DELETE</span> to confirm:
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="Type DELETE here..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                    autoFocus
                  />
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmData(null);
                      setDeleteConfirmText('');
                    }}
                    className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={executeDelete}
                    disabled={deleteConfirmText !== 'DELETE'}
                    className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    Delete Group
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Leave Confirmation Modal */}
        <AnimatePresence>
          {showLeaveConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl"
              >
                <div className="flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mx-auto mb-6">
                  <UserMinus className="w-8 h-8 text-orange-600" />
                </div>
                
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
                  Leave Group
                </h2>
                
                <div className="text-center mb-6">
                  <p className="text-gray-600 mb-4">
                    Are you sure you want to leave <strong>"{leaveConfirmData?.groupName}"</strong>?
                  </p>
                  
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-left">
                    <p className="text-sm text-orange-800 font-medium mb-2">You will:</p>
                    <ul className="text-sm text-orange-700 space-y-1">
                      <li>• Be removed from the group</li>
                      <li>• No longer receive group notifications</li>
                      <li>• Need to be re-invited to rejoin</li>
                      <li>• Keep your friendships with other members</li>
                    </ul>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={() => {
                      setShowLeaveConfirm(false);
                      setLeaveConfirmData(null);
                    }}
                    className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={executeLeave}
                    className="flex-1 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
                  >
                    Leave Group
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

    </div>
  );
};

export default Home; 